@if (!auth.enquiryComplete$()) {
  <div class="mx-auto flex max-w-5xl flex-col space-y-4 p-5">
    <section
      class="flex flex-col gap-4 rounded-2xl border border-slate-800 bg-slate-900 p-5 shadow-lg md:flex-row md:items-center md:justify-between md:p-6"
    >
      <div class="min-w-0 space-y-3">
        <div>
          <h1 class="text-xl font-semibold text-slate-50 md:text-2xl">
            {{ 'WelcomeBack' | localizeText : 'Welcome Back' }},
          </h1>
          <h1 class="text-xl font-bold capitalize text-emerald-300 md:text-2xl">
            {{ userProfile()?.firstName ?? '' }}
          </h1>

          <p class="mt-2 max-w-md text-xs text-slate-300 md:text-sm">
            {{
              'MyOnboarding.Instructions'
                | localizeText
                : 'Continue your journey setup by signing into the iOS or Android application, using your login below'
            }}:
          </p>
        </div>

        @if (vm(); as flow) {
          <div class="space-y-2">
            <div class="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 class="text-sm font-semibold text-slate-100">
                  {{ 'MyOnboarding.Title' | localizeText : 'Journey progress' }}
                </h2>
                <p class="mt-1 text-xs text-slate-400">
                  {{
                    'MyOnboarding.OverallProgress'
                      | localizeText
                      : ("You're " + flow.overallPercent + '% of the way through your setup.')
                  }}
                </p>
              </div>

              <div class="text-right">
                <p class="text-2xl font-bold leading-none text-emerald-400">
                  {{ flow.overallPercent }}%
                </p>
                <p class="text-[10px] uppercase tracking-wide text-slate-500">
                  {{ 'MyOnboarding.CompleteLabel' | localizeText : 'Complete' }}
                </p>
              </div>
            </div>

            <div class="h-2 w-full overflow-hidden rounded-full bg-slate-800">
              <div
                class="h-2 rounded-full bg-emerald-500 transition-all duration-300"
                [style.width.%]="flow.overallPercent"
              ></div>
            </div>
          </div>
        }
      </div>

      <div
        class="mt-3 w-full space-y-2 rounded-xl border border-slate-700 bg-slate-800/70 px-4 py-3 md:mt-0 md:w-auto"
      >
        <p class="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          {{
            'MyOnboarding.LoginLabel'
              | localizeText
              : 'Your app login'
          }}
        </p>

        <div class="flex items-center gap-2">
          <span
            class="inline-flex items-center rounded-md border border-slate-700 bg-slate-900/80 px-3 py-1.5 font-mono text-xs text-emerald-200 md:text-sm"
          >
            {{ username$() }}
          </span>
        </div>

        <p class="text-[11px] text-slate-500">
          {{
            'MyOnboarding.LoginHint'
              | localizeText
              : 'Use this username when signing into the FitMed iOS or Android app.'
          }}
        </p>
      </div>
    </section>

    <div class="mx-auto w-full space-y-4">
      @if (vm(); as flow) {
        <section class="rounded-2xl border border-slate-800 bg-slate-900 p-5">
          <div class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div class="space-y-2">
              <p class="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
                Flow status
              </p>

              <div class="space-y-1">
                <h3 class="text-xl font-semibold text-slate-50 md:text-2xl">
                  {{ flow.title }}
                </h3>
                @if (flow.description) {
                  <p class="text-sm text-slate-400">
                    {{ flow.description }}
                  </p>
                }
                <p class="text-sm text-slate-400">
                  @if (flow.currentStep) {
                    Current step: <span class="font-medium text-slate-200">{{ flow.currentStep.title }}</span>
                  } @else {
                    No active onboarding step is available yet.
                  }
                </p>
                <p class="text-xs text-slate-500">
                  {{ flow.completedSteps }} of {{ flow.totalSteps }} steps complete
                </p>
              </div>
            </div>

            <div class="rounded-2xl border border-slate-800 bg-slate-950/60 px-4 py-3 text-right">
              <p class="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
                Flow progress
              </p>
              <p class="mt-2 text-3xl font-bold leading-none text-emerald-400">
                {{ flow.overallPercent }}%
              </p>
              <p class="mt-1 text-[11px] uppercase tracking-wide text-slate-500">
                Complete
              </p>
            </div>
          </div>

          <div class="mt-4 h-2 w-full overflow-hidden rounded-full bg-slate-800">
            <div
              class="h-2 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-400 transition-all duration-300"
              [style.width.%]="flow.overallPercent"
            ></div>
          </div>

          <div class="mt-5 space-y-3">
            @for (step of flow.steps; track step.id) {
              <article class="rounded-xl border border-slate-800 bg-slate-950/60 p-3">
                <div class="flex items-start justify-between gap-3">
                  <div class="min-w-0">
                    <div class="flex flex-wrap items-center gap-2">
                      <h4 class="truncate text-sm font-medium text-slate-50">
                        {{ step.title }}
                      </h4>

                      @if (step.isCurrent) {
                        <span class="inline-flex items-center rounded-full bg-sky-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-200">
                          {{ 'MyOnboarding.CurrentStep' | localizeText : 'Current step' }}
                        </span>
                      }
                    </div>

                    @if (step.description) {
                      <p class="mt-1 text-xs text-slate-400">
                        {{ step.description }}
                      </p>
                    }
                  </div>

                  <span
                    class="inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide"
                    [ngClass]="{
                      'border border-slate-700 bg-slate-900/70 text-slate-200': step.status === 'completed',
                      'border border-sky-500/30 bg-sky-500/10 text-sky-200': step.status === 'in_progress',
                      'border border-slate-700 bg-slate-800/80 text-slate-300': step.status === 'not_started'
                    }"
                  >
                    @switch (step.status) {
                      @case ('completed') {
                        {{ 'MyOnboarding.StatusCompleted' | localizeText : 'Completed' }}
                      }
                      @case ('in_progress') {
                        {{ 'MyOnboarding.StatusPending' | localizeText : 'In progress' }}
                      }
                      @case ('not_started') {
                        {{ 'MyOnboarding.StatusNotStarted' | localizeText : 'Not started' }}
                      }
                      @default {
                        {{ step.status }}
                      }
                    }
                  </span>
                </div>
              </article>
            }
          </div>
        </section>
      } @else {
        <div class="flex justify-center py-12 text-sm text-slate-500">
          {{
            'MyOnboarding.Loading'
              | localizeText
              : 'Loading journey…'
          }}
        </div>
      }
    </div>
  </div>
}
